import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

type GreenZone = 'left' | 'right';

interface GreenAttack2ZoneScoreButtonsProps {
  onScoreChange: (score: number, tendency?: GreenZone) => void;
}

const GreenAttack2ZoneScoreButtons: React.FC<GreenAttack2ZoneScoreButtonsProps> = ({ onScoreChange }) => {
  const { t } = useTranslation('training');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showZone, setShowZone] = useState(false);

  const handleScoreClick = (score: number) => {
    if (score === 0) {
      onScoreChange(score);
      return;
    }
    
    setSelectedScore(score);
    setShowZone(true);
  };

  const handleZoneClick = (zone: GreenZone) => {
    if (selectedScore !== null) {
      onScoreChange(selectedScore, zone);
      resetSelection();
    }
  };

  const resetSelection = () => {
    setSelectedScore(null);
    setShowZone(false);
  };

  if (showZone) {
    return (
      <div className="space-y-3">
        <div className="text-center text-sm font-medium text-golf-700 mb-2">
          {t('level3.scoring.greenAttack.selectZone')}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => handleZoneClick('left')}
            className="h-16 bg-blue-500 hover:bg-blue-600 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-xs">{t('level3.scoring.greenAttack.left')}</span>
          </Button>
          <Button
            onClick={() => handleZoneClick('right')}
            className="h-16 bg-green-500 hover:bg-green-600 text-white font-semibold flex flex-col items-center justify-center gap-1"
          >
            <ArrowRight className="h-5 w-5" />
            <span className="text-xs">{t('level3.scoring.greenAttack.right')}</span>
          </Button>
        </div>
        <Button
          onClick={resetSelection}
          variant="outline"
          className="w-full"
        >
          {t('level3.scoring.greenAttack.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        onClick={() => handleScoreClick(10)}
        className="w-full h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballIn')}
      </Button>
      <Button
        onClick={() => handleScoreClick(6)}
        className="w-full h-14 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballAt2m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(4)}
        className="w-full h-14 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballAt4m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(2)}
        className="w-full h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballAt6m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(1)}
        className="w-full h-14 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballAt10m')}
      </Button>
      <Button
        onClick={() => handleScoreClick(0)}
        className="w-full h-14 bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-semibold text-lg shadow-md"
      >
        {t('level3.scoring.greenAttack.ballOutside')}
      </Button>
    </div>
  );
};

export default GreenAttack2ZoneScoreButtons;
